import React, { useEffect, useState } from "react";
import AgGridTable from "../components/table/AgGridTable";
import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { ColDef } from "ag-grid-community";
import axiosInstance from "../axios/axios";
import {
  FRAMEWORK_NOT_ENABLED_MESSAGE,
  isFrameworkNotEnabledError,
} from "../shared/hooks/useFrameworks";

import arista_assets from "../assets/arista-assets.svg";
import arista_dark from "../assets/arista-white.svg";
import cisco_assets from "../assets/cisco-assets.svg";
import cisco_dark from "../assets/cisco-white.svg";
import juniper_dark from "../assets/juniper-white.svg";
import juniperLogo from "../assets/juniper.svg";
import paloalto_assets from "../assets/paloalto-assets.svg";
import paloalto_dark from "../assets/paloalto-white.svg";
import ubuntu_white from "../assets/ubuntu_white.svg";
import ubuntu_dark from "../assets/ubuntu_dark.svg";
import windows_light from "../assets/baseVersion/Windows_light.svg";
import windows_dark from "../assets/baseVersion/Windows_dark.svg";
import debian from "../assets/debian.svg";
import debian_dark from "../assets/debian_dark.svg";
import redhat_light from "../assets/redhat_light.svg";
import redhat_dark from "../assets/redhat_dark.svg";
import huawei_light from "../assets/huawei-ar21 1.svg";
import mikrotik from "../assets/mikrotik.svg";
import mikrotik_dark from "../assets/mikrotik_dark.svg";
import huawei_dark from "../assets/huawei-dark.svg";
import edgecore_logo from "../assets/baseVersion/edgecore-logo.svg";
import tplink_light from "../assets/baseVersion/TPLINK_Logo_2 1.svg";
import tplink_dark from "../assets/baseVersion/TPLINK_Logo_dark.svg";
import fortinet_img from "../assets/fortinet.svg";
import fortinet_scan_dark from "../assets/fortinet_scan_dark.svg";
import sophosLogo from "../assets/sophos.svg";
import sophos_dark from "../assets/sophos_dark.svg";
import apple_img from "../assets/apple.svg";
import apple_dark from "../assets/apple_dark.svg";
import dell_logo from "../assets/baseVersion/dell-seeklogo 2.svg";

import syrotech_light from "../assets/syrotech_light.svg";
import syrotech_dark from "../assets/syrotech_dark.svg";
const NistAssetsTable = ({ rowid }: any) => {
  const [isrowData, setRowData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [frameworkDisabled, setFrameworkDisabled] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setFrameworkDisabled(false);
      try {
        const response = await axiosInstance.get(
          `/securityscan/compliance-assets?control_id=${rowid}&module=on-prem`
        );
        setRowData(response.data.data.compliance_assets);
      } catch (error) {
        console.error("Error fetching controls:", error);
        if (isFrameworkNotEnabledError(error)) {
          setRowData([]);
          setFrameworkDisabled(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [rowid]);

  const rowData = isrowData?.map((control) => ({
    assets: control.hostname,
    vendor: control.vendor,
    device_type: control.device_type,
    type: `${control.vendor} - ${control.device_type}`,
    compliance: control.compliance,
    percentage:
      control.recommendations > 0
        ? (control.passed_recommendations / control.recommendations) * 100
        : 0,
    passed_recommendations: control.passed_recommendations,
    recommendations: control.recommendations,
  }));

  const columnDefs: ColDef[] = [
    {
      field: "assets",
      headerName: "Name",
      maxWidth: 200,
      // valueGetter: (params: any) => params.node.rowIndex + 1,
    },

    {
      field: "type",
      headerName: "Type",
      filter: true,
      minWidth: 140,
      cellRenderer: (params: any) => {
        const vendor = params.data?.vendor?.toLowerCase();

        const getImage = () => {
          if (vendor === "cisco") {
            return (
              <img
                src={isDarkMode ? cisco_dark : cisco_assets}
                alt="Cisco"
                style={{ width: "60px", height: "20px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "juniper") {
            return (
              <img
                src={isDarkMode ? juniper_dark : juniperLogo}
                alt="Juniper"
                style={{ width: "60px", height: "20px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "palo alto" || vendor === "paloalto") {
            return (
              <img
                src={isDarkMode ? paloalto_dark : paloalto_assets}
                alt="Palo Alto"
                style={{ width: "60px", height: "20px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "arista") {
            return (
              <img
                src={isDarkMode ? arista_dark : arista_assets}
                alt="Arista"
                style={{ width: "60px", height: "20px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "fortinet") {
            return (
              <img
                src={isDarkMode ? fortinet_scan_dark : fortinet_img}
                alt="Fortinet"
                style={{ width: "50px", height: "24px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "ubuntu") {
            return (
              <img
                src={isDarkMode ? ubuntu_dark : ubuntu_white}
                alt="Ubuntu"
                style={{ width: "60px", height: "22px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "microsoft" || vendor === "windows") {
            return (
              <img
                src={isDarkMode ? windows_dark : windows_light}
                alt="Microsoft"
                style={{ width: "65px", height: "22px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "redhat" || vendor === "rhel" || vendor === "red hat") {
            return (
              <img
                src={isDarkMode ? redhat_dark : redhat_light}
                alt="RedHat"
                style={{ width: "60px", height: "22px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "debian") {
            return (
              <img
                src={isDarkMode ? debian_dark : debian}
                alt="Debian"
                style={{ width: "60px", height: "22px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "huawei") {
            return (
              <img
                src={isDarkMode ? huawei_dark : huawei_light}
                alt="Huawei"
                style={{ width: "60px", height: "22px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "mikrotik") {
            return (
              <img
                src={isDarkMode ? mikrotik_dark : mikrotik}
                alt="Mikrotik"
                style={{ width: "90px", height: "35px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "edgecore") {
            return (
              <img
                src={edgecore_logo}
                alt="Edgecore"
                style={{ width: "80px", height: "30px", objectFit: "contain", filter: isDarkMode ? "brightness(0) invert(1)" : "none" }}
              />
            );
          }
          if (vendor === "tp-link" || vendor === "tplink") {
            return (
              <img
                src={isDarkMode ? tplink_dark : tplink_light}
                alt="TP-Link"
                style={{ width: "80px", height: "30px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "dell") {
            return (
              <img
                src={dell_logo}
                alt="Dell"
                style={{ width: "40px", height: "40px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "syrotech") {
            return (
              <img
                src={isDarkMode ? syrotech_dark : syrotech_light}
                alt="Syrotech"
                style={{ width: "90px", height: "32px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "sophos") {
            return (
              <img
                src={isDarkMode ? sophos_dark : sophosLogo}
                alt="Sophos"
                style={{ width: "60px", height: "22px", objectFit: "contain" }}
              />
            );
          }
          if (vendor === "apple" || vendor === "macos") {
            return (
              <img
                src={isDarkMode ? apple_dark : apple_img}
                alt="Apple"
                style={{ width: "28px", height: "28px", objectFit: "contain" }}
              />
            );
          }
          return null;
        };

        const image = getImage();
        return (
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            {image || (
              <Typography
                title={params.data?.vendor}
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {params.data?.vendor}
              </Typography>
            )}
          </Box>
        );
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },

    {
      field: "compliance",
      headerName: "Compliance",
      filter: true,
      minWidth: 50,
      cellRenderer: (params: any) => {
        const value = Math.round(Number(params.value)) || 0;

        const progressColor =
          value >= 75 ? "green" : value >= 50 ? "orange" : "red";

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              whiteSpace: "nowrap",
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 30,
                height: 30,
              }}
            >
              {/* Background circle */}
              <CircularProgress
                variant="determinate"
                value={100}
                size={30}
                thickness={5}
                sx={{ color: "#c5c5d2" }}
              />
              {/* Foreground progress */}
              <CircularProgress
                variant="determinate"
                value={value}
                size={30}
                thickness={5}
                sx={{
                  color: progressColor,
                  position: "absolute",
                  left: 0,
                  top: 0,
                }}
              />
            </Box>
            <Typography variant="body2" fontWeight={600}>
              {`${value}%`}
            </Typography>
          </Box>
        );
      },

      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
      // cellRenderer: (params: any) => {
      //   const isManual = params.value === "Manual";
      //   const color = isManual ? "#F7941F" : "#696595";

      //   return (
      //     <div
      //       style={{
      //         border: `1px solid ${color}`,
      //         borderRadius: 4,
      //         padding: "2px 6px",
      //         lineHeight: "1.5",
      //         whiteSpace: "nowrap",
      //         fontSize: 12,
      //         color: color,
      //       }}
      //     >
      //       {params.value}
      //     </div>
      //   );
      // },
    },

    {
      field: "percentage",
      headerName: "Benchmark",
      filter: true,
      minWidth: 100,
      cellRenderer: (params: any) => {
        const passedCount = params.data.passed_recommendations;
        const compliance_count = params.data.recommendations;
        const progressValue = params.data.percentage || 0;

        return (
          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: "#f5f5f5",
                    "& .MuiLinearProgress-bar": {
                      bgcolor:
                        progressValue >= 70
                          ? "#4caf50"
                          : progressValue >= 40
                          ? "#ff9800"
                          : "#f44336",
                    },
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                {`${passedCount}/${compliance_count}`}
              </Typography>
            </Box>
          </Box>
        );
      },

      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
  ];

  return (
    <div
      style={{
        height: "calc(74vh - 95px)",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        scrollbarWidth: "none",
      }}
    >
      <AgGridTable
        columnDefs={columnDefs}
        data={rowData}
        showSearch
        isRowSelectable
        height={"calc(72vh - 110px)"}
        isLoading={isLoading}
        noRowsMessage={frameworkDisabled ? FRAMEWORK_NOT_ENABLED_MESSAGE : undefined}
      />
    </div>
  );
};

export default NistAssetsTable;
